// frontend/src/components/Common/InfoHint.jsx
//
// Small ⓘ icon for card headers: hover shows the explanation instantly on
// desktop, click/tap toggles it (the only way on touch screens). The popover
// explains what the card shows and how to read it, in plain language.
import React, { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';

const InfoHint = ({ title, children, align = 'center' }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // tap-away closes the popover on touch screens
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  const pos = align === 'left'
    ? 'left-0'
    : align === 'right'
      ? 'right-0'
      : 'left-1/2 -translate-x-1/2';

  return (
    <span ref={rootRef} className="relative inline-flex group/info shrink-0">
      <button
        type="button"
        aria-label={`About ${title}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`inline-flex items-center justify-center rounded-full transition-colors ${
          open ? 'text-emerald-600' : 'text-gray-300 hover:text-gray-500'}`}
      >
        <Info size={13} />
      </button>
      <span
        className={`absolute top-full ${pos} mt-1.5 z-50 w-60 max-w-[78vw] rounded-lg border border-gray-200 bg-white p-3 text-left shadow-xl ${
          open ? 'block' : 'hidden group-hover/info:block'}`}
      >
        <span className="block text-xs font-semibold text-gray-900 mb-1">{title}</span>
        <span className="block text-[11px] leading-relaxed text-gray-600">{children}</span>
      </span>
    </span>
  );
};

export default InfoHint;
